<template>
    <div>
        <!-- Render all inputs in this group -->
        <div :key="input_field.placeholder">
            <div class="row">
                <div class="col-12">
                    <div class="d-flex justify-content-between align-items-center">
                        <label class="form-label mb-1 w-100">
                            <span>
                                {{input_field.name}}
                                <em v-if="input_field.optional === 1">(Optional)</em>
                                <i class="fas text-primary fa-exclamation-circle info-icon ms-1 align-self-center" data-bs-toggle="tooltip" data-bs-placement="bottom" :title="input_field.description"></i>
                            </span>

                            <button type="button" v-if="input_field.repeatable && input_field.type !== 'image'" class="btn btn-sm btn-secondary py-0 float-end" @click.prevent="addRepeatableOption()"
                                :disabled="is_generating || !if_allow_edit">
                                <i class="fa fa-plus me-0"></i> Add Option
                            </button>
                        </label>
                    </div>

                    <div>
                        <div :class="getErrorForMaxLength(input_field)" :maxlength="input_field.max_length" :currentlength="(local_value != null) ? local_value.length: 0"
                            v-if="(input_field.type === 'text' || input_field.type === 'link')">
                            <div v-if="input_field.repeatable">
                                <div v-for="(value, valueIndex) in (Array.isArray(local_value) ? local_value : [local_value])" :key="input_field.placeholder + '_' + valueIndex" class="d-flex mb-2">
                                    <input type="text" v-model="local_value[valueIndex]" :maxlength="input_field.max_length"
                                        :disabled="is_generating || !if_allow_edit"
                                        class="form-control"
                                        :class="[is_generating ? 'is-generating' : '', error !== '' ? 'is-invalid' : '']"
                                        @change="inputFieldValueChange()" aria-label="Local Value[value Index]">
                                    <button type="button" class="btn btn-secondary ms-2" @click.prevent="removeRepeatableOption(valueIndex)" data-bs-toggle="tooltip" title="Remove"
                                        :disabled="is_generating || !if_allow_edit" v-if="Array.isArray(local_value) && local_value.length > 1">
                                        <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                    </button>
                                </div>
                            </div>
                            <input v-else type="text" v-model="local_value" :maxlength="input_field.max_length"
                                :disabled="is_generating || !if_allow_edit"
                                class="form-control"
                                :class="[is_generating ? 'is-generating' : '', error !== '' ? 'is-invalid' : '']"
                                @change="inputFieldValueChange()" aria-label="Local Value">
                        </div>
                        <div v-else-if="(input_field.type === 'textarea')">
                            <div v-if="input_field.repeatable">
                                <div v-for="(value, valueIndex) in (Array.isArray(local_value) ? local_value : [local_value])" :key="input_field.placeholder + '_' + valueIndex" class="mb-2">
                                    <div class="d-flex mb-2">
                                        <textarea :id="input_field.placeholder+'-'+valueIndex"
                                            v-model="local_value[valueIndex]"
                                            :maxlength="input_field.max_length"
                                            :disabled="is_generating || !if_allow_edit"
                                            class="full-height form-control"
                                            :class="[is_generating ? 'is-generating' : '', (error !== '') ? 'is-invalid' : '']"
                                            @change="inputFieldValueChange()"
                                            ref="textarea"></textarea>
                                        <button type="button" class="btn btn-secondary ms-2" @click.prevent="removeRepeatableOption(valueIndex)" data-bs-toggle="tooltip" title="Remove"
                                            :disabled="is_generating || !if_allow_edit" v-if="Array.isArray(local_value) && local_value.length > 1">
                                            <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <textarea v-else :id="input_field.placeholder"
                                v-model="local_value"
                                :maxlength="input_field.max_length"
                                :disabled="is_generating || !if_allow_edit"
                                class="full-height form-control"
                                :class="[is_generating ? 'is-generating' : '', (error !== '') ? 'is-invalid' : '']"
                                @change="inputFieldValueChange()"
                                ref="textarea"></textarea>
                        </div>

                        <div v-else-if="input_field.type === 'image'" class="image-array-section">
                            <div class="d-flex flex-wrap gap-2 align-items-start">
                                <!-- Display uploaded images as thumbnails -->
                                <div v-for="(image_url, image_index) in getValidImages()" :key="image_index" 
                                    class="position-relative image-thumbnail-wrapper" 
                                    :class="{ 'is-generating': isImageGenerating(image_url) }"
                                    style="max-width: 200px;">
                                    <img 
                                        :src="image_url" 
                                        alt="Uploaded image" 
                                        class="image-thumbnail"
                                        style="height: 100px; width: auto; max-width: 100%; object-fit: contain; border-radius: 20px; cursor: pointer;"
                                        @click.prevent="openImagePreview(image_index)"
                                    />
                                    <!-- Generating overlay -->
                                    <div v-if="isImageGenerating(image_url) && !(typeof image_url === 'string' && image_url.startsWith('generating_'))" class="generating-overlay"></div>
                                    <div class="position-absolute top-0 end-0 d-flex gap-1 m-1" style="z-index: 2;">
                                        <button 
                                            v-if="client?.image_preference !== 'none'"
                                            type="button" 
                                            class="btn btn-icon-only-sm btn-secondary p-1 mt-1" 
                                            data-bs-toggle="tooltip" title="Refine"
                                            @click.stop.prevent="openRefineImageModal(image_index)"
                                            :disabled="is_generating || !if_allow_edit || isImageGenerating(image_url)"
                                            style="display: flex; align-items: center; justify-content: center;"
                                        >
                                            <img class="icon" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" alt="">
                                        </button>
                                        <button 
                                            type="button" 
                                            class="btn btn-icon-only-sm btn-secondary p-1 mt-1" 
                                            data-bs-toggle="tooltip" title="Remove"
                                            @click.stop.prevent="removeImage(image_index)"
                                            :disabled="is_generating || !if_allow_edit || isImageGenerating(image_url)"
                                            style="display: flex; align-items: center; justify-content: center;"
                                        >
                                            <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                        </button>
                                    </div>
                                </div>
                                
                                <!-- Plus button to add new image -->
                                <button 
                                    type="button" 
                                    class="btn btn-secondary d-flex align-items-center justify-content-center add-image-btn"
                                    data-bs-toggle="tooltip" :title="'Add '+input_field.name"
                                    @click.prevent="openImageModal()"
                                    :disabled="is_generating || !if_allow_edit"
                                    style="width: 100px; height: 100px; border-radius: 20px;"
                                >
                                    <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="Add image" style="width: 24px; height: 24px;">
                                </button>
                            </div>
                            
                            <!-- Image Upload Modal - Teleported to body for nested modal support -->
                            <Teleport to="body">
                                <div class="modal fade" :id="'image-upload-modal-' + input_field.placeholder" tabindex="-1" :aria-labelledby="'image-upload-modal-label-' + input_field.placeholder" aria-hidden="true" data-bs-backdrop="static">
                                    <div class="modal-dialog modal-dialog-centered">
                                        <div class="modal-content">
                                            <div class="modal-header border-bottom-0 pb-0">
                                                <h5 class="modal-title" :id="'image-upload-modal-label-' + input_field.placeholder">Add an Image</h5>
                                                <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close">
                                                    <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                                                </button>
                                            </div>
                                            <div class="modal-body">
                                                <!-- Image Style & Prompt (only if AI is allowed) -->
                                                <div v-if="isAiImageGenerationAllowed" class="mb-4">
                                                    
                                                    <label :for="'image-prompt-' + input_field.placeholder" class="form-label">Describe your image:</label>
                                                    <textarea 
                                                        :id="'image-prompt-' + input_field.placeholder"
                                                        v-model="modalImagePrompt"
                                                        class="form-control" 
                                                        rows="3"
                                                        placeholder="e.g., Create an image with..."
                                                        :disabled="is_generating || !if_allow_edit"
                                                        @input="handlePromptInput"
                                                    ></textarea>
                                                    <label :for="'image-style-' + input_field.placeholder" class="form-label mt-2">Image style</label>
                                                    <select
                                                        :id="'image-style-' + input_field.placeholder"
                                                        v-model="modalImageStyleId"
                                                        class="form-select mb-2"
                                                        :disabled="is_generating || !if_allow_edit"
                                                     aria-label="Image style">
                                                        <option value="" disabled>Select a style...</option>
                                                        <option v-for="style in playbookImageStyles" :key="style.id" :value="style.id">
                                                            {{ style.name }}
                                                        </option>
                                                    </select>
                                                    <p class="mb-3 small">
                                                        <a href="https://help.avidai.com/hc/en-us/articles/49174183508499-Image-Style-Guide" target="_blank" rel="noopener noreferrer">See examples of each style</a>
                                                    </p>
                                                </div>

                                                <!-- Divider with OR text (only if AI is allowed) -->
                                                <div v-if="isAiImageGenerationAllowed" class="d-flex align-items-center mb-3">
                                                    <hr class="flex-grow-1">
                                                    <span class="mx-3 text-muted">OR</span>
                                                    <hr class="flex-grow-1">
                                                </div>

                                                <!-- Image Upload Section -->
                                                <div>
                                                    <label class="form-label">Upload an image:</label>
                                                    <ImageDropzone
                                                        :key="'image-dropzone-' + input_field.placeholder + '-' + modalImageResetKey"
                                                        v-model="modalImageValue"
                                                        :minHeight="140"
                                                        class="image-dropzone-wrapper"
                                                        :id="'modal-image-dropzone-' + input_field.placeholder"
                                                        :disabled="is_generating || !if_allow_edit || (isAiImageGenerationAllowed && modalImagePrompt.trim().length > 0)"
                                                        :if_thumbnail="false"
                                                        @update:modelValue="handleModalImageUpdate"
                                                    />
                                                </div>
                                            </div>
                                            <div class="modal-footer">
                                                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                                                <button 
                                                    type="button" 
                                                    class="btn btn-primary" 
                                                    @click="confirmImageAdd"
                                                    :disabled="is_generating || !if_allow_edit || (!modalImageValue && (isAiImageGenerationAllowed ? (!modalImagePrompt.trim() || !modalImageStyleId) : true))"
                                                >
                                                    <img class="icon me-2" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" v-if="isAiImageGenerationAllowed && modalImagePrompt.trim()" alt="">
                                                    <img class="icon me-2" src="/img/icons/avid-icons/plus.svg" style="width: 20px; height: 20px;" v-else alt="">
                                                    {{ (isAiImageGenerationAllowed && modalImagePrompt.trim()) ? 'Generate' : 'Add Image' }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Image Preview Modal - Teleported to body for nested modal support -->
                                <div class="modal fade" :id="'image-preview-modal-' + input_field.placeholder" tabindex="-1" :aria-labelledby="'image-preview-modal-label-' + input_field.placeholder" aria-hidden="true" data-bs-backdrop="static">
                                    <div class="modal-dialog modal-dialog-centered modal-lg">
                                        <div class="modal-content">
                                            <div class="modal-header border-bottom-0 pb-0">
                                                <h5 class="modal-title" :id="'image-preview-modal-label-' + input_field.placeholder">
                                                    <span v-if="previewModalMode === 'preview'">Preview</span>
                                                    <span v-else>Refine {{ input_field.name }}</span>
                                                </h5>
                                                <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close" @click="closeImagePreviewModal">
                                                    <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                                                </button>
                                            </div>
                                            <div class="modal-body px-4 pb-4">
                                                <!-- Image Display (Preview Mode Only) -->
                                                <div v-if="previewModalMode === 'preview' && previewImageUrl" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
                                                    <img 
                                                        :src="previewImageUrl" 
                                                        alt="Preview image" 
                                                        class="img-fluid"
                                                        style="max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 20px;"
                                                    />
                                                </div>
                                                
                                                <!-- Refine Feedback Form (Refine Mode Only) -->
                                                <div v-if="previewModalMode === 'refine'">
                                                    <label :for="'regenerate_feedback_' + input_field.placeholder" class="form-label">Describe the changes you'd like:</label>
                                                    <textarea 
                                                        :id="'regenerate_feedback_' + input_field.placeholder"
                                                        v-model="regenerate_feedback_data.feedback_text"
                                                        class="form-control" 
                                                        rows="4"
                                                        placeholder="e.g., Make it more focused on this segment, use this webpage as a reference, talk about this initiative..."
                                                        :class="{ 'is-invalid': regenerate_feedback_data.error_message }"
                                                    ></textarea>
                                                    <div v-if="regenerate_feedback_data.error_message" class="invalid-feedback d-block">
                                                        {{ regenerate_feedback_data.error_message }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="previewModalMode === 'refine'" class="modal-footer border-top-0 pt-0">
                                                <div class="w-100 d-flex justify-content-end gap-2">
                                                    <button 
                                                        type="button" 
                                                        class="btn btn-primary" 
                                                        @click.prevent="submitRefineFeedback()"
                                                        :disabled="is_generating || !if_allow_edit || !regenerate_feedback_data.feedback_text.trim()"
                                                    >
                                                        <img class="icon me-2" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" alt="">
                                                        Refine
                                                    </button>
                                                    <button 
                                                        type="button" 
                                                        class="btn btn-secondary" 
                                                        @click.prevent="deletePreviewImage()"
                                                        :disabled="is_generating || !if_allow_edit"
                                                    >
                                                        <img class="icon me-2" src="/img/icons/avid-icons/trash.svg" alt="">
                                                        Delete Image
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Teleport>
                        </div>

                        <FileDropzone 
                            v-else-if="input_field.type === 'file'" 
                            :client-url="client.url"
                            :on-file-uploaded="(file) => handleFileUploaded(file)"
                            :class="is_generating ? 'is-generating' : ''"
                            :disabled="is_generating || !if_allow_edit"
                        />

                        <wysiwyg-editor-advanced v-else-if="input_field.type === 'wysiwyg'" :input_value="local_value" :input_index="0" :asset_id="input_field.placeholder"
                            @wysiwygValueChange="wysiwygValueChange" :if_allow_edit="if_allow_edit && !is_generating" :class="is_generating ? 'is-generating' : ''"></wysiwyg-editor-advanced>

                        <select v-else-if="input_field.type === 'dropdown'" v-model="local_value" class="form-select w-auto form-control"
                            :class="[is_generating ? 'is-generating' : '',
                                    (error !== '') ? 'is-invalid' : '']"
                            :disabled="is_generating || !if_allow_edit"
                            @change="inputFieldValueChange()" aria-label="Local Value">
                            <option value="" disabled selected></option>
                            <option v-for="option in input_field.options" :key="option" :value="option">{{ option }}</option>
                        </select>

                        <!-- Group Type Handling -->
                        <div v-else-if="input_field.type === 'group'" class="group-container">
                            <div v-if="input_field.repeatable">
                                <div v-for="(groupInstance, groupIndex) in local_value" :key="'group_' + groupIndex" class="group-instance">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h6 class="mb-0">{{ input_field.name }} #{{ groupIndex + 1 }}</h6>
                                        <button 
                                            type="button" 
                                            class="btn btn-sm btn-secondary" 
                                            @click.prevent="removeGroupInstance(groupIndex)" 
                                            data-bs-toggle="tooltip" 
                                            title="Remove"
                                            :disabled="is_generating || !if_allow_edit || local_value.length === 1"
                                            v-if="local_value.length > 1">
                                            <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                        </button>
                                    </div>
                                    <div v-for="groupField in group_fields" :key="groupField.placeholder" class="mb-2">
                                        <label class="form-label mb-1">
                                            {{ groupField.name }}
                                            <em v-if="groupField.optional === 1">(Optional)</em>
                                        </label>
                                        <input 
                                            v-if="groupField.type === 'text' || groupField.type === 'link'"
                                            type="text" 
                                            v-model="local_value[groupIndex][groupField.placeholder]"
                                            :maxlength="groupField.max_length"
                                            :disabled="is_generating || !if_allow_edit"
                                            class="form-control"
                                            @change="inputFieldValueChange()" aria-label="Text input">
                                        <textarea 
                                            v-else-if="groupField.type === 'textarea'"
                                            v-model="local_value[groupIndex][groupField.placeholder]"
                                            :maxlength="groupField.max_length"
                                            :disabled="is_generating || !if_allow_edit"
                                            class="form-control"
                                            @change="inputFieldValueChange()"></textarea>
                                        <select 
                                            v-else-if="groupField.type === 'dropdown'" 
                                            v-model="local_value[groupIndex][groupField.placeholder]"
                                            class="form-select form-control"
                                            :disabled="is_generating || !if_allow_edit"
                                            @change="inputFieldValueChange()" aria-label="Placeholder]">
                                            <option value="" disabled selected></option>
                                            <option v-for="option in groupField.options" :key="option" :value="option">{{ option }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="group-instance">
                                <div v-for="groupField in group_fields" :key="groupField.placeholder" class="mb-2">
                                    <label class="form-label mb-1">
                                        {{ groupField.name }}
                                        <em v-if="groupField.optional === 1">(Optional)</em>
                                    </label>
                                    <input 
                                        v-if="groupField.type === 'text' || groupField.type === 'link'"
                                        type="text" 
                                        v-model="local_value[groupField.placeholder]"
                                        :maxlength="groupField.max_length"
                                        :disabled="is_generating || !if_allow_edit"
                                        class="form-control"
                                        @change="inputFieldValueChange()" aria-label="Text input">
                                    <textarea 
                                        v-else-if="groupField.type === 'textarea'"
                                        v-model="local_value[groupField.placeholder]"
                                        :maxlength="groupField.max_length"
                                        :disabled="is_generating || !if_allow_edit"
                                        class="form-control"
                                        @change="inputFieldValueChange()"></textarea>
                                    <select 
                                        v-else-if="groupField.type === 'dropdown'" 
                                        v-model="local_value[groupField.placeholder]"
                                        class="form-select form-control"
                                        :disabled="is_generating || !if_allow_edit"
                                        @change="inputFieldValueChange()" aria-label="Placeholder]">
                                        <option value="" disabled selected></option>
                                        <option v-for="option in groupField.options" :key="option" :value="option">{{ option }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="text-danger" v-if="(error !== null && error !== '')" v-html="error">                    </div>
                </div>
            </div>
        </div>

        <!-- Refine Image Modal - Teleported to body to avoid nesting -->
        <Teleport to="body">
            <div 
                v-if="input_field.type === 'image'"
                class="modal fade" 
                :id="`refine-image-modal-${input_field.placeholder}`" 
                tabindex="-1" 
                aria-labelledby="refineImageModalLabel" 
                aria-hidden="true" 
                data-bs-backdrop="static" 
                data-bs-keyboard="false"
            >
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="refineImageModalLabel">
                                <img src="/img/icons/avid-icons/edna.svg" class="icon me-2" alt=""></img>
                                Refine Image
                            </h5>
                            <button type="button" class="btn btn-white close-modal" @click="closeRefineImageModal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                            </button>
                        </div>
                        <div class="modal-body">
                            <p class="text-muted mb-3">
                                Describe how you would like to {{ asset?.source === 'generated' ? 'refine' : 'generate' }} the image.
                            </p>
                            <textarea 
                                class="form-control" 
                                name="refine_image_instructions" 
                                placeholder="Type or paste your guidelines..."
                                v-model="refineImageInstructions" 
                                rows="4"
                            ></textarea>
                            <div class="form-check mt-3" v-if="hasMultipleImagesForSameStyle">
                                <input 
                                    class="form-check-input" 
                                    type="checkbox" 
                                    id="updateRelatedImages" 
                                    v-model="updateRelatedImages"
                                 aria-label="Checkbox">
                                <label class="form-check-label" for="updateRelatedImages">
                                    Update related images
                                </label>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button 
                                type="button" 
                                class="btn btn-secondary" 
                                @click="closeRefineImageModal"
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                class="btn btn-primary" 
                                @click="submitRefineImage"
                                :disabled="!refineImageInstructions || !refineImageInstructions.trim()"
                            >
                                <img class="icon me-2" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" alt="">
                                {{ asset?.source === 'generated' ? 'Refine' : 'Generate' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { usePlaybookStore } from '../../stores/playbook';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    setup() {
        const playbookStore = usePlaybookStore();
        const { showNotification } = useNotifications();
        return { playbookStore, showNotification };
    },
    
    props: {
        modelValue: {
            type: [String, Array, Object],
            default: null
        },
        input_field: {
            type: Object,
            required: true
        },
        group_fields: {
            type: Array,
            default: () => []
        },
        client: {
            type: Object,
            default: () => ({})
        },
        asset: {
            type: Object,
            default: null
        },
        is_generating: {
            type: Boolean,
            default: false
        },
        if_allow_edit: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            local_value: null,
            error: "",
            modalImageValue: '',
            modalImagePrompt: '',
            modalImageResetKey: 0,
            previewImageUrl: '',
            previewImageIndex: null,
            previewModalMode: 'preview', // 'preview' or 'refine'
            regenerate_feedback_data: {
                image_index: null,
                feedback_text: '',
                error_message: ''
            },
            refineImageInstructions: '',
            currentRefineImageIndex: null,
            updateRelatedImages: false,
            generatingImages: new Set(), // Track which image URLs are currently generating
            playbookImageStyles: [],
            modalImageStyleId: '',
        };
    },
    beforeMount() {
        this.setLocalValue();
    },
    mounted() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        for (let i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                    tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                    tooltipList[index].hide();
                });
            })(i);
        }
    },
    beforeUnmount() {
        // Clean up tooltips
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltipTriggerList.forEach(tooltipTriggerEl => {
            const tooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
            if (tooltip) {
                tooltip.dispose();
            }
        });
        
        // Clean up image modals if they exist
        if (this.input_field.type === 'image') {
            // Clean up upload modal
            const uploadModalId = 'image-upload-modal-' + this.input_field.placeholder;
            const uploadModalElement = document.getElementById(uploadModalId);
            if (uploadModalElement) {
                const uploadModal = window.bootstrap.Modal.getInstance(uploadModalElement);
                if (uploadModal) {
                    uploadModal.hide();
                    uploadModal.dispose();
                }
            }
            
            // Clean up preview modal
            const previewModalId = 'image-preview-modal-' + this.input_field.placeholder;
            const previewModalElement = document.getElementById(previewModalId);
            if (previewModalElement) {
                const previewModal = window.bootstrap.Modal.getInstance(previewModalElement);
                if (previewModal) {
                    previewModal.hide();
                    previewModal.dispose();
                }
            }
            
            // Clean up refine image modal
            const refineModalId = 'refine-image-modal-' + this.input_field.placeholder;
            const refineModalElement = document.getElementById(refineModalId);
            if (refineModalElement) {
                const refineModal = window.bootstrap.Modal.getInstance(refineModalElement);
                if (refineModal) {
                    refineModal.hide();
                    refineModal.dispose();
                }
            }
        }
    },
    computed: {
        isAiImageGenerationAllowed() {
            return this.client?.image_preference === 'ai_allowed';
        }
    },
    watch: {
        modelValue(newValue, oldValue) {
            // Only update if the value actually changed from outside the component
            // This prevents re-renders when the user is typing
            if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                // Check if the change came from this component's own input
                if (JSON.stringify(newValue) === JSON.stringify(this.local_value)) {
                    return;
                }
                
                // For image types, clear generating state for images that are no longer in the array
                if (this.input_field.type === 'image' && Array.isArray(oldValue) && Array.isArray(newValue)) {
                    this.clearGeneratingStateForRemovedImages(oldValue, newValue);
                }
                
                this.setLocalValue();

                if(this.local_value === null || this.local_value === '')
                    this.error = "";
                else
                    this.hasErrors();
            }
        },
        // Watch asset.inputs to detect updates from AssetUpdated event
        'asset.inputs': {
            handler(newInputs, oldInputs) {
                if (this.input_field.type === 'image' && newInputs) {
                    // Find the input field for this placeholder
                    const inputField = newInputs.find(input => input.placeholder === this.input_field.placeholder);
                    
                    if (inputField && Array.isArray(inputField.value)) {
                        // Only clear generating state when the array has actually changed and placeholders were replaced
                        if (oldInputs) {
                            const oldInputField = oldInputs.find(input => input.placeholder === this.input_field.placeholder);
                            if (oldInputField && Array.isArray(oldInputField.value)) {
                                // Find placeholders that were in old inputs
                                const oldPlaceholders = oldInputField.value.filter(url => 
                                    typeof url === 'string' && url.startsWith('generating_')
                                );
                                
                                // Only clear generating state if placeholders were actually replaced with real images
                                // Check if the array changed and placeholders are gone
                                const arrayChanged = JSON.stringify(oldInputField.value) !== JSON.stringify(inputField.value);
                                
                                if (arrayChanged && oldPlaceholders.length > 0) {
                                    // Check each placeholder to see if it was replaced
                                    oldPlaceholders.forEach(placeholderUrl => {
                                        // Check if this placeholder is no longer in new inputs
                                        const placeholderStillExists = inputField.value.some(newUrl => newUrl === placeholderUrl);
                                        if (!placeholderStillExists) {
                                            // Placeholder was removed - only clear if array length maintained or increased
                                            // (indicating it was replaced, not just removed)
                                            if (inputField.value.length >= oldInputField.value.length) {
                                                // Placeholder was replaced with real image, clear generating state
                                                this.generatingImages.delete(placeholderUrl);
                                            }
                                        }
                                    });
                                }
                                
                                // Also clear generating state for replaced images (non-placeholder)
                                this.clearGeneratingStateForRemovedImages(oldInputField.value, inputField.value);
                            }
                        }
                        
                        // Sync local_value if it's out of date
                        // This will replace placeholders with actual image URLs when they're generated
                        if (JSON.stringify(this.local_value) !== JSON.stringify(inputField.value)) {
                            this.setLocalValue();
                        }
                    }
                }
            },
            deep: true
        }
    },
    methods: {
        setLocalValue(){
            // Handle group type
            if (this.input_field.type === 'group') {
                if (this.input_field.repeatable) {
                    // For repeatable groups, ensure it's an array of objects
                    if (Array.isArray(this.modelValue) && this.modelValue.length > 0) {
                        // Filter out null values and replace with empty group instances
                        this.local_value = this.modelValue.map(item => {
                            if (item === null || item === undefined || typeof item !== 'object') {
                                const emptyGroupInstance = {};
                                this.group_fields.forEach(field => {
                                    emptyGroupInstance[field.placeholder] = '';
                                });
                                return emptyGroupInstance;
                            }
                            return JSON.parse(JSON.stringify(item));
                        });
                    } else {
                        // Initialize with one empty group instance
                        const emptyGroupInstance = {};
                        this.group_fields.forEach(field => {
                            emptyGroupInstance[field.placeholder] = '';
                        });
                        this.local_value = [emptyGroupInstance];
                    }
                } else {
                    // For non-repeatable groups, ensure it's a single object
                    if (typeof this.modelValue === 'object' && this.modelValue !== null && !Array.isArray(this.modelValue)) {
                        this.local_value = {...this.modelValue};
                    } else {
                        // Initialize with empty object
                        const emptyGroupInstance = {};
                        this.group_fields.forEach(field => {
                            emptyGroupInstance[field.placeholder] = '';
                        });
                        this.local_value = emptyGroupInstance;
                    }
                }
                return;
            }

            //Value may be an array or an object
            if (Array.isArray(this.modelValue))
                this.local_value = JSON.parse(JSON.stringify(this.modelValue));
            //If it is an object, it is a new asset
            else if(typeof this.modelValue === 'object' && this.modelValue != null)
                this.local_value = {...this.modelValue};
            else if(this.input_field.repeatable)
                if(this.modelValue == null)
                    this.local_value = [''];
                else
                    this.local_value = [this.modelValue];
            else
                this.local_value = this.modelValue;

            if(this.input_field.type == 'image') {
                if (!Array.isArray(this.local_value)) {
                    this.local_value = [this.local_value];
                }
                // Filter out null/empty values from existing data
                this.local_value = this.local_value.filter(img => img !== null && img !== '' && img !== undefined);
            }
            if(this.input_field.type == 'file' && !Array.isArray(this.local_value))
                this.local_value = [this.local_value];

        },
        wysiwygValueChange(newValueWithIndex){
            this.local_value = newValueWithIndex.value;
            this.inputFieldValueChange();
        },
        inputFieldValueChange(){
            // For image types, filter out null/empty values before emitting
            let valueToEmit = this.local_value;
            if (this.input_field.type === 'image' && Array.isArray(this.local_value)) {
                valueToEmit = this.local_value.filter(img => img !== null && img !== '' && img !== undefined);
                // If array becomes empty, emit empty array instead of null
                if (valueToEmit.length === 0) {
                    valueToEmit = [];
                }
            }
            
            this.$emit('update:modelValue', valueToEmit);
            this.$emit('change');
        },
        getErrorForMaxLength() {
            var classes = ''
            if (this.local_value != null && this.input_field.max_length != null && this.local_value.length > this.input_field.max_length){
                this.error = 'The maximum number of characters is ' + this.input_field.max_length + ". When generating text, the AI has a hard time following character limit rules&mdash;please adjust the text above."
            }
            else if(this.local_value != null && this.local_value != "")
                this.error = "";


            if (this.input_field.max_length != null)
                classes += 'max-warning'

            if(this.error != null && this.error != '' )
                classes +=' is-invalid';

            return classes;
        },
        hasErrors(){
            // Clear the error message
            this.error = "";

            // Handle null exceptions
            if (this.local_value == null) {
                this.local_value = '';
            }
            if (this.local_value == "" && this.input_field.optional == 0) {
                this.error = this.input_field.name + " is required.";
            } else if (this.input_field.max_length != null && this.local_value.length > this.input_field.max_length) {
                this.error = "Maximum length exceeded. The max length for " + this.input_field.name + " is " + this.input_field.max_length;
            }

            return this.error != "";
        },
        getValidImages() {
            if (!Array.isArray(this.local_value)) {
                return [];
            }
            return this.local_value.filter(img => img !== null && img !== '' && img !== undefined);
        },
        async openImageModal() {
            // Reset the modal image value and prompt
            this.modalImageValue = '';
            this.modalImagePrompt = '';
            this.modalImageResetKey += 1; // Force ImageDropzone to re-render

            if (this.isAiImageGenerationAllowed && this.playbookImageStyles.length === 0) {
                await this.fetchPlaybookImageStyles();
            }
            if (this.isAiImageGenerationAllowed && this.playbookImageStyles.length > 0 && !this.modalImageStyleId) {
                this.modalImageStyleId = this.playbookImageStyles[0].id;
            }
            
            this.$nextTick(() => {
                const modalId = 'image-upload-modal-' + this.input_field.placeholder;
                const modalElement = document.getElementById(modalId);
                if (modalElement) {
                    // Dispose any existing modal instance
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }
                    
                    // Create new modal instance with proper nested modal configuration
                    const modal = new window.bootstrap.Modal(modalElement, {
                        backdrop: 'static',
                        keyboard: false,
                        focus: true
                    });
                    
                    // Setup event listener to reset modal values when modal is closed
                    const handleHidden = () => {
                        this.modalImageValue = '';
                        this.modalImagePrompt = '';
                        this.modalImageStyleId = this.playbookImageStyles.length > 0 ? this.playbookImageStyles[0].id : '';
                        modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                    };
                    modalElement.addEventListener('hidden.bs.modal', handleHidden);
                    
                    // Show the modal
                    modal.show();
                    
                    // Initialize tooltips in the modal
                    this.$nextTick(() => {
                        const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                            if (existingTooltip) {
                                existingTooltip.dispose();
                            }
                            new window.bootstrap.Tooltip(tooltipTriggerEl);
                        });
                    });
                }
            });
        },
        async fetchPlaybookImageStyles() {
            try {
                const response = await window.axios.get('/api/playbooks/image_styles');
                if (response.data?.data) {
                    this.playbookImageStyles = response.data.data;
                    if (this.playbookImageStyles.length > 0 && !this.modalImageStyleId) {
                        this.modalImageStyleId = this.playbookImageStyles[0].id;
                    }
                }
            } catch (error) {
                console.error('Error fetching playbook image styles:', error);
            }
        },
        handlePromptInput() {
            // Clear image when prompt is entered
            if (this.modalImagePrompt.trim().length > 0) {
                this.modalImageValue = '';
                this.modalImageResetKey += 1; // Force ImageDropzone to reset
            }
        },
        handleModalImageUpdate(value) {
            if (value && value !== '') {
                // Process the image URL
                value = value.replace("%2F", "/");
                value = value.startsWith("http") ? value : 'https:' + value;
                this.modalImageValue = value;
                
                // Clear prompt when image is uploaded
                this.modalImagePrompt = '';
            }
        },
        async confirmImageAdd() {
            // If prompt is provided, generate image
            if (this.modalImagePrompt.trim()) {
                await this.generateImageFromPrompt();
            } 
            // If image is uploaded, add it directly
            else if (this.modalImageValue) {
                // Process the image URL
                let value = this.modalImageValue.replace("%2F", "/");
                value = value.startsWith("http") ? value : 'https:' + value;
                
                // Initialize local_value as array if needed
                if (!Array.isArray(this.local_value)) {
                    this.local_value = [];
                }
                
                // Add the new image to the array
                this.local_value.push(value);
                
                // Close the modal
                const modalId = 'image-upload-modal-' + this.input_field.placeholder;
                const modalElement = document.getElementById(modalId);
                if (modalElement) {
                    const modal = window.bootstrap.Modal.getInstance(modalElement);
                    if (modal) {
                        modal.hide();
                    }
                }
                
                // Reset modal values
                this.modalImageValue = '';
                this.modalImagePrompt = '';
                this.modalImageResetKey += 1;
                this.inputFieldValueChange();
            }
        },
        async generateImageFromPrompt() {
            if (!this.modalImagePrompt.trim()) {
                return;
            }
            
            try {
                const playbook = this.playbookStore.getSelectedPlaybook;
                if (!playbook) {
                    return;
                }

                // Mark asset as generating immediately for responsive UI
                if (this.asset?.id) {
                    this.playbookStore.updateAsset(this.asset.id, { status: 'generating' });
                    this.asset.status = 'generating';
                }
                
                // Close the modal
                const modalId = 'image-upload-modal-' + this.input_field.placeholder;
                const modalElement = document.getElementById(modalId);
                if (modalElement) {
                    const modal = window.bootstrap.Modal.getInstance(modalElement);
                    if (modal) {
                        modal.hide();
                    }
                }
                
                // Reset modal values
                const promptText = this.modalImagePrompt.trim();
                this.modalImageValue = '';
                this.modalImagePrompt = '';
                this.modalImageResetKey += 1;
                
                // Call the generate endpoint
                const response = await window.axios.post(
                    `/api/playbooks/${this.client.url}/playbooks/${playbook.url}/assets/${this.asset.id}/images/generate`,
                    {
                        placeholder: this.input_field.placeholder,
                        prompt: promptText,
                        playbook_image_style_id: this.modalImageStyleId ? parseInt(this.modalImageStyleId, 10) : null
                    }
                );
                
                // Show success notification
                this.showNotification({
                    icon: '/img/icons/nav-icons/Playbooks.svg',
                    text: 'Image generation has started for <strong>' + this.asset.name + '</strong>!'
                });
                
            } catch (error) {
                console.error('Error generating image:', error);
                
                // Show error notification
                this.showNotification({
                    icon: '/img/icons/avid-icons/danger.svg',
                    text: 'Failed to generate image'
                });
            }
        },
        removeImage(image_index) {
            const validImages = this.getValidImages();
            if (Array.isArray(this.local_value) && validImages.length > 0) {
                // Find the actual index in local_value array
                let validIndex = 0;
                for (let i = 0; i < this.local_value.length; i++) {
                    if (this.local_value[i] !== null && this.local_value[i] !== '' && this.local_value[i] !== undefined) {
                        if (validIndex === image_index) {
                            this.local_value.splice(i, 1);
                            break;
                        }
                        validIndex++;
                    }
                }
                
                // Ensure we always have an array (even if empty)
                if (this.local_value.length === 0) {
                    this.local_value = [];
                }
                
                this.inputFieldValueChange();
                this.$forceUpdate();
            }
        },
        openImagePreview(image_index) {
            const validImages = this.getValidImages();
            if (validImages.length > 0 && image_index < validImages.length) {
                this.previewImageUrl = validImages[image_index];
                this.previewImageIndex = image_index;
                this.previewModalMode = 'preview'; // Set to preview mode
                
                this.$nextTick(() => {
                    const modalId = 'image-preview-modal-' + this.input_field.placeholder;
                    const modalElement = document.getElementById(modalId);
                    if (modalElement) {
                        // Dispose any existing modal instance
                        const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                        if (existingModal) {
                            existingModal.dispose();
                        }
                        
                        // Create new modal instance with proper nested modal configuration
                        const modal = new window.bootstrap.Modal(modalElement, {
                            backdrop: 'static',
                            keyboard: false,
                            focus: true
                        });
                        
                        // Setup event listener to reset preview data when modal is closed
                        const handleHidden = () => {
                            this.closeImagePreviewModal();
                            modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                        };
                        modalElement.addEventListener('hidden.bs.modal', handleHidden);
                        
                        // Show the modal
                        modal.show();
                        
                        // Initialize tooltips in the modal
                        this.$nextTick(() => {
                            const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                            [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }
                                new window.bootstrap.Tooltip(tooltipTriggerEl);
                            });
                        });
                    }
                });
            }
        },
        deletePreviewImage() {
            if (this.previewImageIndex !== null) {
                // Use the same removeImage logic
                this.removeImage(this.previewImageIndex);
                
                // Close the preview modal
                this.closeImagePreviewModal();
            }
        },
        openRefineModal(image_index) {
            const validImages = this.getValidImages();
            if (validImages.length > 0 && image_index < validImages.length) {
                this.previewImageUrl = validImages[image_index];
                this.previewImageIndex = image_index;
                this.previewModalMode = 'refine'; // Set to refine mode
                this.regenerate_feedback_data.image_index = image_index;
                this.regenerate_feedback_data.feedback_text = '';
                this.regenerate_feedback_data.error_message = '';
                
                this.$nextTick(() => {
                    const modalId = 'image-preview-modal-' + this.input_field.placeholder;
                    const modalElement = document.getElementById(modalId);
                    if (modalElement) {
                        // Dispose any existing modal instance
                        const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                        if (existingModal) {
                            existingModal.dispose();
                        }
                        
                        // Create new modal instance with proper nested modal configuration
                        const modal = new window.bootstrap.Modal(modalElement, {
                            backdrop: 'static',
                            keyboard: false,
                            focus: true
                        });
                        
                        // Setup event listener to reset preview data when modal is closed
                        const handleHidden = () => {
                            this.closeImagePreviewModal();
                            modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                        };
                        modalElement.addEventListener('hidden.bs.modal', handleHidden);
                        
                        // Show the modal
                        modal.show();
                        
                        // Focus on textarea after modal is shown
                        this.$nextTick(() => {
                            const textareaId = 'regenerate_feedback_' + this.input_field.placeholder;
                            const textarea = document.getElementById(textareaId);
                            if (textarea) {
                                textarea.focus();
                            }
                            
                            // Initialize tooltips in the modal
                            const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                            [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }
                                new window.bootstrap.Tooltip(tooltipTriggerEl);
                            });
                        });
                    }
                });
            }
        },
        closeImagePreviewModal() {
            // Reset preview data
            this.previewImageUrl = '';
            this.previewImageIndex = null;
            this.previewModalMode = 'preview';
            this.regenerate_feedback_data.image_index = null;
            this.regenerate_feedback_data.feedback_text = '';
            this.regenerate_feedback_data.error_message = '';
            
            // Close the modal
            const modalId = 'image-preview-modal-' + this.input_field.placeholder;
            const modalElement = document.getElementById(modalId);
            if (modalElement) {
                const modal = window.bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }
            }
        },
        submitRefineFeedback() {
            // Validate feedback text
            if (!this.regenerate_feedback_data.feedback_text.trim()) {
                this.regenerate_feedback_data.error_message = 'Please describe how you would like the image to be changed.';
                return;
            }

            // Clear any previous error
            this.regenerate_feedback_data.error_message = '';

            // Emit event to parent component with refine feedback
            // The parent component can handle the actual refine/regenerate logic
            this.$emit('refine-image', {
                image_index: this.regenerate_feedback_data.image_index,
                feedback: this.regenerate_feedback_data.feedback_text.trim(),
                input_field_placeholder: this.input_field.placeholder
            });

            // Close the modal
            this.closeImagePreviewModal();
        },
        handleFileUploaded(file) {
            // Initialize local_value as array if null
            if (this.local_value === null) {
                this.local_value = [];
            }

            // Add the file to the array
            this.local_value.unshift(file);
            
            // Update the UI and notify parent
            this.$forceUpdate();
            this.inputFieldValueChange();
        },
        addRepeatableOption() {
            // Handle repeatable group
            if (this.input_field.type === 'group') {
                this.addGroupInstance();
                return;
            }

            // Handle regular repeatable input
            if (!Array.isArray(this.local_value)) {
                this.local_value = [this.local_value];
            }
            this.local_value.push('');
            //this.inputFieldValueChange();
            this.$forceUpdate();
        },
        removeRepeatableOption(index) {

            // Handle regular repeatable input
            if (Array.isArray(this.local_value)) {
                this.local_value.splice(index, 1);
                if (this.local_value.length === 0) {
                    this.local_value.push('');
                }
            }
            this.inputFieldValueChange();
            this.$forceUpdate();
        },
        
        addGroupInstance() {
            // Create an empty object for the new group instance
            const newGroupInstance = {};
            this.group_fields.forEach(field => {
                newGroupInstance[field.placeholder] = '';
            });
            
            if (!Array.isArray(this.local_value)) {
                this.local_value = [newGroupInstance];
            } else {
                this.local_value.push(newGroupInstance);
            }
            
            this.inputFieldValueChange();
            this.$forceUpdate();
        },
        
        removeGroupInstance(index) {
            if (Array.isArray(this.local_value) && this.local_value.length > 1) {
                this.local_value.splice(index, 1);
                this.inputFieldValueChange();
                this.$forceUpdate();
            }
        },
        canRefineImage(imageIndex) {
            // Only show refine button if image exists and is an object with url or gcs_path (generated image)
            const image = this.local_value[imageIndex];
            return image && typeof image === 'object' && (image.url || image.gcs_path);
        },
        hasMultipleImagesForSameStyle() {
            // Check if there are multiple images in the array
            if (!Array.isArray(this.local_value) || this.local_value.length < 2) {
                return false;
            }
            
            // Check if asset has image_style_ids configured
            if (!this.asset || !this.asset.playbook_type_asset_type?.pivot?.image_style_ids) {
                return false;
            }
            
            // Check if input field has multiple image_ratios
            if (!this.input_field.image_ratios || 
                (Array.isArray(this.input_field.image_ratios) && this.input_field.image_ratios.length < 2)) {
                return false;
            }
            
            // Count how many images have the same structure (indicating same style)
            const imageObjects = this.local_value.filter(img => img && typeof img === 'object' && (img.url || img.gcs_path));
            return imageObjects.length >= 2;
        },
        openRefineImageModal(imageIndex) {
            this.currentRefineImageIndex = imageIndex;
            this.refineImageInstructions = '';
            this.updateRelatedImages = false;
            
            this.$nextTick(() => {
                const modalElement = document.getElementById(`refine-image-modal-${this.input_field.placeholder}`);
                if (modalElement) {
                    // Dispose any existing modal instance
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }
                    
                    // Create new modal instance
                    const modal = new window.bootstrap.Modal(modalElement, {
                        backdrop: 'static',
                        keyboard: false,
                        focus: true
                    });
                    
                    // Show the modal
                    modal.show();
                    
                    // Initialize tooltips in the modal
                    this.$nextTick(() => {
                        const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                            if (existingTooltip) {
                                existingTooltip.dispose();
                            }
                            new window.bootstrap.Tooltip(tooltipTriggerEl);
                        });
                    });
                }
            });
        },
        closeRefineImageModal() {
            const modalElement = document.getElementById(`refine-image-modal-${this.input_field.placeholder}`);
            if (modalElement) {
                const modal = window.bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                } else {
                    // If no instance exists, dispose any existing and create new to hide
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }
                }
            }
            // Only reset the refine modal state, don't touch parent modal
            this.refineImageInstructions = '';
            this.updateRelatedImages = false;
            this.currentRefineImageIndex = null;
        },
        async submitRefineImage() {
            if (!this.refineImageInstructions || !this.refineImageInstructions.trim()) {
                return;
            }
            
            const imageUrl = this.getValidImages()[this.currentRefineImageIndex];
            if (!imageUrl) {
                return;
            }
            
            // Store instructions before closing modal (which resets it)
            const instructions = this.refineImageInstructions.trim();
            const updateRelated = this.updateRelatedImages;
            
            try {
                const playbook = this.playbookStore.getSelectedPlaybook;
                if (!playbook) {
                    return;
                }

                // Mark asset as generating immediately for responsive UI
                if (this.asset?.id) {
                    this.playbookStore.updateAsset(this.asset.id, { status: 'generating' });
                    this.asset.status = 'generating';
                }
                
                // Mark image(s) as generating
                this.markImageAsGenerating(imageUrl, updateRelated);
                
                // Close modal
                this.closeRefineImageModal();
                
                // Show success notification
                this.showNotification({
                    icon: '/img/icons/nav-icons/Playbooks.svg',
                    text: 'Image regeneration has started for <strong>' + this.asset.name + '</strong>!'
                });
                
                const response = await window.axios.post(
                    `/api/playbooks/${this.client.url}/playbooks/${playbook.url}/assets/${this.asset.id}/images/regenerate`,
                    {
                        image_url: imageUrl,
                        placeholder: this.input_field.placeholder,
                        additional_instructions: instructions,
                        update_related_images: updateRelated
                    }
                );
                
            } catch (error) {
                console.error('Error refining image:', error);
                
                // Show error notification
                this.showNotification({
                    icon: '/img/icons/avid-icons/danger.svg',
                    text: 'The image for <strong>' + this.asset.name + '</strong> failed to regenerate. Please try again.'
                });
            }
        },
        // Check if an image URL is currently generating
        isImageGenerating(imageUrl) {
            // Check if it's a placeholder URL (starts with "generating_")
            if (typeof imageUrl === 'string' && imageUrl.startsWith('generating_')) {
                return true;
            }
            return this.generatingImages.has(imageUrl);
        },
        // Extract filename pattern from image URL
        // Pattern: asset_image_{asset_id}_{image_style_id}
        getImageFilenamePattern(imageUrl) {
            if (!imageUrl || typeof imageUrl !== 'string') {
                return null;
            }
            
            // Match pattern: asset_image_{asset_id}_{image_style_id}
            const match = imageUrl.match(/asset_image_(\d+)_(\d+)_/);
            if (match) {
                return `asset_image_${match[1]}_${match[2]}`;
            }
            
            return null;
        },
        // Get all related image URLs (same filename pattern)
        getRelatedImageUrls(imageUrl) {
            const pattern = this.getImageFilenamePattern(imageUrl);
            if (!pattern) {
                return [];
            }
            
            const validImages = this.getValidImages();
            return validImages.filter(img => {
                const imgPattern = this.getImageFilenamePattern(img);
                return imgPattern === pattern;
            });
        },
        // Mark image(s) as generating
        markImageAsGenerating(imageUrl, includeRelated) {
            this.generatingImages.add(imageUrl);
            
            if (includeRelated) {
                const relatedUrls = this.getRelatedImageUrls(imageUrl);
                relatedUrls.forEach(url => {
                    this.generatingImages.add(url);
                });
            }
        },
        // Clear generating state for images that are no longer in the array
        clearGeneratingStateForRemovedImages(oldImages, newImages) {
            const oldSet = new Set(oldImages);
            const newSet = new Set(newImages);
            
            // Remove generating state for images that are no longer present
            this.generatingImages.forEach(imageUrl => {
                if (oldSet.has(imageUrl) && !newSet.has(imageUrl)) {
                    this.generatingImages.delete(imageUrl);
                }
            });
        },
        // Clear all generating state
        clearGeneratingState() {
            this.generatingImages.clear();
        }
    }
});
</script>